import React, { useEffect, useState } from 'react';
import { ArrowLeft, Check, X, Info, Calendar, Clock, DollarSign, Zap, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * /book-prototype — Internal preview for Ayumi.
 * Toggle between visual mockups of Acuity (current), Square Appointments,
 * Fresha, and SimplyBook.me so she can see how /book would feel with each.
 * These are styled previews — not live embeds (would require real accounts).
 */

const PROVIDERS = [
  {
    key: 'acuity',
    name: 'Acuity (current)',
    brand: 'bg-gray-900 text-white',
    accent: 'teal',
    cost: '~$20–$61 / month',
    processing: '2.9% + 30¢ (Stripe/Square)',
    bestFor: 'Clinical-leaning solo + wellness practices that need forms, packages, memberships, gift certificates',
    pros: ['Robust intake forms', 'Gift certificate redemption', 'Packages / memberships', 'HIPAA option (Powerhouse)', 'Reliable embed'],
    cons: ['Most expensive option', 'UI feels dated to some clients', 'Mobile embed clunky'],
  },
  {
    key: 'square',
    name: 'Square Appointments',
    brand: 'bg-black text-white',
    accent: 'blue',
    cost: 'Free for solo',
    processing: '2.6% + 10¢ in-person · 2.9% + 30¢ online',
    bestFor: 'Solo practitioners who already use (or will use) Square for in-person card payments',
    pros: ['Free for solo', 'Square POS + card reader integration', 'Afterpay built in', 'Clean, modern UI', 'Marketing/retention tools'],
    cons: ['No native SOAP notes', 'Weaker intake forms than Acuity', 'Gift certificates basic', 'Locked into Square payments'],
  },
  {
    key: 'fresha',
    name: 'Fresha',
    brand: 'bg-rose-50 text-gray-900',
    accent: 'rose',
    cost: 'Free scheduling',
    processing: '2.19% + 20¢ (Fresha payments only)',
    bestFor: 'Therapists wanting a consumer marketplace boost (clients can discover you on Fresha)',
    pros: ['Free scheduling forever', 'Lowest card fee (2.19%)', 'Consumer marketplace exposure', 'Beautiful mobile UX', 'Smart no-show protection'],
    cons: ['Marketplace pressures price competition', 'Must use Fresha payments to use the system', 'No real SOAP/clinical notes', 'Weaker form customization'],
  },
  {
    key: 'simplybook',
    name: 'SimplyBook.me',
    brand: 'bg-teal-900 text-white',
    accent: 'teal',
    cost: 'Free tier · $9.90 / $29.90 / $59.90 paid tiers',
    processing: 'Varies (connects to Stripe/PayPal)',
    bestFor: 'An Acuity-style drop-in replacement with the most flexibility and lowest cost',
    pros: ['Closest Acuity-style embed', 'Very low-cost paid tiers', 'Flexible custom features', 'Supports packages, memberships, gift cards', 'HIPAA add-on available'],
    cons: ['Settings UI is deep / takes time to configure', 'Branding/customization locked on free tier', 'Less polished out of the box than Fresha/Square'],
  },
];

const SESSIONS = [
  { id: 's30', name: '30-Minute Massage', duration: '30 min', price: 55 },
  { id: 's60', name: '60-Minute Massage', duration: '60 min', price: 110 },
  { id: 's90', name: '90-Minute Massage', duration: '90 min', price: 160 },
  { id: 'facial', name: 'Signature Facial', duration: '60 min', price: 95 },
];

const DAYS = ['Mon 17', 'Tue 18', 'Wed 19', 'Thu 20', 'Fri 21', 'Sat 22'];
const SLOTS = ['10:00 AM', '11:30 AM', '1:00 PM', '2:30 PM', '4:00 PM', '5:30 PM'];

const AccentButton = ({ accent, children, selected, onClick, ...rest }) => {
  const base = 'py-2.5 px-3 rounded-md text-sm font-medium transition-all border';
  const colorMap = {
    teal: {
      selected: 'bg-teal-600 text-white border-teal-600 shadow-sm',
      idle: 'bg-white text-teal-700 border-teal-200 hover:border-teal-400',
    },
    blue: {
      selected: 'bg-blue-600 text-white border-blue-600 shadow-sm',
      idle: 'bg-white text-blue-700 border-blue-200 hover:border-blue-400',
    },
    rose: {
      selected: 'bg-rose-600 text-white border-rose-600 shadow-sm',
      idle: 'bg-white text-rose-700 border-rose-200 hover:border-rose-400',
    },
  };
  const cls = colorMap[accent] || colorMap.teal;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} ${selected ? cls.selected : cls.idle}`}
      {...rest}
    >
      {children}
    </button>
  );
};

/** Visual mockup for a provider's booking widget — styled to feel like that provider */
const ProviderMock = ({ provider }) => {
  const [selectedSession, setSelectedSession] = useState('s60');
  const [selectedDay, setSelectedDay] = useState(1);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Different providers have slightly different layouts — vary the UI to match
  if (provider.key === 'square') {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-testid="mock-square">
        <div className="bg-black text-white px-6 py-3 text-xs tracking-widest uppercase flex items-center justify-between">
          <span>Square · Book an appointment</span>
          <span className="font-bold">■</span>
        </div>
        <div className="p-6">
          <h4 className="font-semibold text-gray-800 mb-3 text-sm">1. Select a service</h4>
          <div className="space-y-2 mb-6">
            {SESSIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setSelectedSession(s.id)}
                className={`w-full text-left px-4 py-3 rounded-lg border transition-all flex items-center justify-between ${
                  selectedSession === s.id ? 'border-black bg-gray-50' : 'border-gray-200 hover:border-gray-400'
                }`}
              >
                <div>
                  <p className="font-medium text-gray-900 text-sm">{s.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{s.duration}</p>
                </div>
                <span className="font-semibold text-gray-900">${s.price}</span>
              </button>
            ))}
          </div>
          <h4 className="font-semibold text-gray-800 mb-3 text-sm">2. Pick a time</h4>
          <div className="flex gap-2 mb-4 overflow-x-auto">
            {DAYS.map((d, i) => (
              <AccentButton key={d} accent="blue" selected={selectedDay === i} onClick={() => setSelectedDay(i)}>
                {d}
              </AccentButton>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {SLOTS.map((slot) => (
              <AccentButton key={slot} accent="blue" selected={selectedSlot === slot} onClick={() => setSelectedSlot(slot)}>
                {slot}
              </AccentButton>
            ))}
          </div>
          <button className="w-full mt-6 bg-black text-white rounded-lg py-3 font-medium hover:bg-gray-800">
            Continue
          </button>
        </div>
      </div>
    );
  }

  if (provider.key === 'fresha') {
    return (
      <div className="bg-white rounded-2xl border border-rose-100 overflow-hidden shadow-sm" data-testid="mock-fresha">
        <div className="bg-gradient-to-r from-rose-50 to-pink-50 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs tracking-widest uppercase text-rose-600 font-semibold">Fresha</p>
            <p className="text-sm text-gray-700">Ayumi Suzuki Massage · Midtown Sacramento</p>
          </div>
          <div className="text-xs text-right">
            <p className="text-rose-700 font-semibold">★ 5.0</p>
            <p className="text-gray-500">47 reviews</p>
          </div>
        </div>
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-3 mb-6">
            {SESSIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setSelectedSession(s.id)}
                className={`text-left px-4 py-3 rounded-2xl border-2 transition-all ${
                  selectedSession === s.id ? 'border-rose-500 bg-rose-50' : 'border-gray-100 hover:border-rose-200'
                }`}
              >
                <p className="font-medium text-gray-900 text-sm">{s.name}</p>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-gray-500">{s.duration}</p>
                  <p className="font-semibold text-rose-600">${s.price}</p>
                </div>
              </button>
            ))}
          </div>
          <div className="flex gap-2 mb-4 overflow-x-auto">
            {DAYS.map((d, i) => (
              <AccentButton key={d} accent="rose" selected={selectedDay === i} onClick={() => setSelectedDay(i)}>
                {d}
              </AccentButton>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {SLOTS.map((slot) => (
              <AccentButton key={slot} accent="rose" selected={selectedSlot === slot} onClick={() => setSelectedSlot(slot)}>
                {slot}
              </AccentButton>
            ))}
          </div>
          <button className="w-full mt-6 bg-rose-600 text-white rounded-full py-3 font-medium hover:bg-rose-700">
            Continue to book
          </button>
          <p className="text-xs text-center text-gray-500 mt-3">Secured by Fresha · No account required</p>
        </div>
      </div>
    );
  }

  if (provider.key === 'simplybook') {
    return (
      <div className="bg-white rounded-xl border border-teal-100 overflow-hidden shadow-sm" data-testid="mock-simplybook">
        <div className="bg-teal-900 text-white px-6 py-3">
          <p className="text-xs tracking-widest uppercase text-teal-200">SimplyBook.me</p>
          <p className="text-sm">Online booking · Ayumi Suzuki Massage</p>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
            <span className="px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 font-semibold">Service</span>
            <span>→</span>
            <span>Date & time</span>
            <span>→</span>
            <span>Details</span>
          </div>
          <div className="space-y-2 mb-6">
            {SESSIONS.map((s) => (
              <label
                key={s.id}
                className={`flex items-center justify-between px-4 py-3 rounded-lg border cursor-pointer transition-all ${
                  selectedSession === s.id ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-teal-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    checked={selectedSession === s.id}
                    onChange={() => setSelectedSession(s.id)}
                    className="accent-teal-600"
                  />
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{s.name}</p>
                    <p className="text-xs text-gray-500">{s.duration}</p>
                  </div>
                </div>
                <span className="font-semibold text-teal-700">${s.price}</span>
              </label>
            ))}
          </div>
          <div className="grid grid-cols-6 gap-1 mb-4">
            {DAYS.map((d, i) => (
              <button
                key={d}
                onClick={() => setSelectedDay(i)}
                className={`py-2 text-xs rounded border ${
                  selectedDay === i ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-700 border-gray-200'
                }`}
              >
                {d}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {SLOTS.map((slot) => (
              <AccentButton key={slot} accent="teal" selected={selectedSlot === slot} onClick={() => setSelectedSlot(slot)}>
                {slot}
              </AccentButton>
            ))}
          </div>
          <button className="w-full mt-6 bg-teal-700 text-white rounded-lg py-3 font-medium hover:bg-teal-800">
            Next
          </button>
        </div>
      </div>
    );
  }

  // acuity (default)
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm" data-testid="mock-acuity">
      <div className="bg-gray-900 text-white px-6 py-3">
        <p className="text-sm font-medium">Schedule Appointment</p>
        <p className="text-xs text-gray-400">Ayumi Suzuki Massage Therapy</p>
      </div>
      <div className="p-6">
        <p className="text-sm font-medium text-gray-800 mb-3">Select an appointment type</p>
        <div className="space-y-2 mb-6">
          {SESSIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSelectedSession(s.id)}
              className={`w-full text-left px-4 py-3 rounded border transition-all ${
                selectedSession === s.id ? 'border-teal-600 bg-teal-50' : 'border-gray-200 hover:border-gray-400'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{s.name}</p>
                  <p className="text-xs text-gray-500">{s.duration} · $ {s.price}</p>
                </div>
                <span className="text-xs text-teal-600 underline">Select</span>
              </div>
            </button>
          ))}
        </div>
        <p className="text-sm font-medium text-gray-800 mb-3">Choose a date and time</p>
        <div className="flex gap-2 mb-4 overflow-x-auto">
          {DAYS.map((d, i) => (
            <AccentButton key={d} accent="teal" selected={selectedDay === i} onClick={() => setSelectedDay(i)}>
              {d}
            </AccentButton>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2">
          {SLOTS.map((slot) => (
            <AccentButton key={slot} accent="teal" selected={selectedSlot === slot} onClick={() => setSelectedSlot(slot)}>
              {slot}
            </AccentButton>
          ))}
        </div>
        <button className="w-full mt-6 bg-teal-600 text-white rounded py-2.5 text-sm font-medium hover:bg-teal-700">
          Continue
        </button>
      </div>
    </div>
  );
};

const BookPrototypePage = () => {
  const [active, setActive] = useState('square');

  useEffect(() => {
    const prev = document.title;
    document.title = 'Book Page Prototype | Ayumi Suzuki';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.title = prev;
      document.head.removeChild(meta);
    };
  }, []);

  const provider = PROVIDERS.find((p) => p.key === active);

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-rose-50" data-testid="book-prototype-page">
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-teal-600 transition-colors duration-300 group"
            data-testid="back-home-link"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" />
            <span>Back to Home</span>
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-teal-700 mb-2">
            <Zap className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase tracking-widest">For Ayumi · Internal preview</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-serif text-gray-800 mb-3">
            Book Page Prototype
          </h1>
          <p className="text-gray-600 max-w-3xl">
            Visual preview of how <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">/book</code> would feel with each scheduling tool. These are styled mockups, not live embeds — the real embed from the provider you pick will drop into exactly this slot.
          </p>
        </div>

        {/* Provider toggle */}
        <div
          className="flex flex-wrap gap-2 mb-8 bg-white p-2 rounded-full shadow-sm border border-gray-100 w-fit"
          data-testid="provider-toggle"
        >
          {PROVIDERS.map((p) => (
            <button
              key={p.key}
              onClick={() => setActive(p.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                active === p.key ? 'bg-teal-600 text-white shadow' : 'text-gray-600 hover:text-teal-700'
              }`}
              data-testid={`toggle-${p.key}`}
            >
              {p.name}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Mock */}
          <div className="lg:col-span-3">
            <ProviderMock provider={provider} />
            <p className="text-xs text-gray-500 mt-3 italic text-center">
              This is a visual mock to show the look & feel — the real {provider.name} embed replaces this at launch.
            </p>
          </div>

          {/* Pros / cons / cost card */}
          <div className="lg:col-span-2 space-y-6" data-testid="provider-info-panel">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-teal-700 font-semibold mb-2">
                <Info className="w-3.5 h-3.5" />
                Overview
              </div>
              <h2 className="text-xl font-serif text-gray-800 mb-3">{provider.name}</h2>
              <p className="text-sm text-gray-600 leading-relaxed">{provider.bestFor}</p>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-gray-500 font-semibold mb-1">
                    <DollarSign className="w-3.5 h-3.5" /> Monthly cost
                  </p>
                  <p className="text-gray-800 font-medium" data-testid="cost-value">{provider.cost}</p>
                </div>
                <div>
                  <p className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-gray-500 font-semibold mb-1">
                    <Calendar className="w-3.5 h-3.5" /> Card processing
                  </p>
                  <p className="text-gray-800 font-medium">{provider.processing}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <p className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-emerald-700 font-semibold mb-3">
                <Check className="w-3.5 h-3.5" /> Pros
              </p>
              <ul className="space-y-2">
                {provider.pros.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm text-gray-700">
                    <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <p className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-rose-700 font-semibold mb-3">
                <X className="w-3.5 h-3.5" /> Trade-offs
              </p>
              <ul className="space-y-2">
                {provider.cons.map((c) => (
                  <li key={c} className="flex items-start gap-2 text-sm text-gray-700">
                    <AlertCircle className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Decision summary strip */}
        <div className="mt-16 bg-white rounded-3xl shadow-sm border border-gray-100 p-8" data-testid="decision-summary">
          <h3 className="text-xl font-serif text-gray-800 mb-4">If I had to pick for your practice today</h3>
          <ul className="space-y-3 text-sm text-gray-700 max-w-3xl">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-teal-100 text-teal-700 text-xs flex items-center justify-center font-semibold">A</span>
              <p>
                <strong>Keep Acuity.</strong> You're already set up, your clients know the flow, and the gift certificate hand-off we just built fits beside Acuity cleanly. The only real reason to switch is cost.
              </p>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-teal-100 text-teal-700 text-xs flex items-center justify-center font-semibold">B</span>
              <p>
                <strong>Save money → Square Appointments.</strong> Free for solo, strong in-person payment integration, modern UI. Best overall value if you also use Square for the physical card reader.
              </p>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-teal-100 text-teal-700 text-xs flex items-center justify-center font-semibold">C</span>
              <p>
                <strong>Save money + find new clients → Fresha.</strong> Free, lowest card fee, has a consumer marketplace that can send new bookings your way. Downside: you're exposed to Fresha's marketing/discount prompts in the UI.
              </p>
            </li>
          </ul>
          <div className="mt-6 pt-6 border-t border-gray-100 flex items-center gap-2 text-sm text-teal-700">
            <Clock className="w-4 h-4" />
            <span>Switching takes ~1–2 hours: new account → import services → swap the embed on /book. I can do it in one pass whenever you decide.</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookPrototypePage;
